import React, { useMemo, useState } from "react"
import Chart from "react-apexcharts"
import moment from "moment"
import { FaDotCircle, FaInfoCircle } from "react-icons/fa"
import { getSameWeekNextYear, getSameWeekPreviousYear, getWeekTooltipLabel } from "../../utils/dateUtil"

const SuccessRateChart = ({
  data,
  previousData,
  maxValue = 280000,
  showWeekCard = false,
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [showFormula, setShowFormula] = useState(false)
  const [currentWeekInput, setCurrentWeekInput] = useState("")
  const [prevWeekInput, setPrevWeekInput] = useState("")
  // If sortOrder is controlled from parent (HistoryPage), use that; else fall back to local state
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const salesRevenue = data || []

  const currentYear = salesRevenue?.length
    ? moment(salesRevenue[0].date).year()
    : moment().year()

  const previousYear = previousData?.length
    ? moment(previousData[0].date).year()
    : currentYear - 1

  // ✅ Union of BOTH years' actual ISO weeks — sorted chronologically
  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    salesRevenue.forEach(item => weekSet.add(moment(item.date).isoWeek()))
    previousData?.forEach(item => weekSet.add(moment(item.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [salesRevenue, previousData])

  // ✅ Current year: ISO week → amount
  const currentByWeek = useMemo(() => {
    const map = {}
    salesRevenue.forEach(item => {
      map[moment(item.date).isoWeek()] = Number(item.amount) || 0
    })
    return map
  }, [salesRevenue])

  // ✅ Previous year: ISO week → amount
  const prevByWeek = useMemo(() => {
    const map = {}
    previousData?.forEach(item => {
      map[moment(item.date).isoWeek()] = Number(item.amount) || 0
    })
    return map
  }, [previousData])

  // ✅ Date lookups by ISO week (current year)
  const dateByWeek = useMemo(() => {
    const map = {}
    salesRevenue.forEach(item => {
      map[moment(item.date).isoWeek()] = moment(item.date).format("DD-MM-YYYY")
    })
    return map
  }, [salesRevenue])

  // ✅ Date lookups by ISO week (previous year)
  const prevDateByWeek = useMemo(() => {
    const map = {}
    previousData?.forEach(item => {
      map[moment(item.date).isoWeek()] = moment(item.date).format("DD-MM-YYYY")
    })
    return map
  }, [previousData])

  // ✅ Apply Sort Order to weeks.
  // Sorting key = max revenue across BOTH years for that week so both lines move together.
  // - "descending": position 0 = highest revenue week, last = lowest
  // - "ascending":  position 0 = lowest revenue week, last = highest
  // - "chronological": Week 1 → Week 52 (default)
  const displayWeeks = useMemo(() => {
    if (sortOrder === "ascending") {
      return [...allWeeks].sort((a, b) => {
        const valA = Math.max(currentByWeek[a] ?? 0, prevByWeek[a] ?? 0)
        const valB = Math.max(currentByWeek[b] ?? 0, prevByWeek[b] ?? 0)
        return valA !== valB ? valA - valB : a - b
      })
    }
    if (sortOrder === "descending") {
      return [...allWeeks].sort((a, b) => {
        const valA = Math.max(currentByWeek[a] ?? 0, prevByWeek[a] ?? 0)
        const valB = Math.max(currentByWeek[b] ?? 0, prevByWeek[b] ?? 0)
        return valA !== valB ? valB - valA : a - b
      })
    }
    return allWeeks
  }, [allWeeks, currentByWeek, prevByWeek, sortOrder])

  // ✅ Build series arrays — both series use the same displayWeeks order
  const currentValues = displayWeeks.map(w => currentByWeek[w] ?? null)
  const prevValues    = displayWeeks.map(w => prevByWeek[w] ?? null)
  // X-axis labels: show the ISO week number for each position
  const categories    = displayWeeks.map(w => `${w}`)

  const series = [
    { name: `Current Year (${currentYear})`,  data: currentValues },
    { name: `Previous Year (${previousYear})`, data: prevValues },
  ]

  const options = {
    chart: {
      type: "area",
      height: 350,
      toolbar: { show: false },
      background: "transparent",
      foreColor: "#1a1a2e",
      animations: { enabled: true, speed: 400 },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      curve: "smooth",
      width: [2.5, 2.5],
      dashArray: [0, 6],
    },
    markers: {
      size: [4, 3],
      colors: ["#fff", "#fff"],
      strokeColors: ["#4361ee", "#4CBB17"],
      strokeWidth: 2,
      hover: { size: 6 },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.02,
        stops: [0, 50, 100],
        colorStops: [
          { offset: 0, color: "#4361ee", opacity: 0.35 },
          { offset: 100, color: "#ddd", opacity: 0.02 },
        ],
      },
      opacity: [0.35, 0],
    },
    colors: ["#4361ee", "#4CBB17"],
    xaxis: {
      // categories drives actual data alignment; overwriteCategories shows week numbers as labels
      categories,
      overwriteCategories: categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      labels: {
        rotate: -45,
        style: { colors: "#1a1a2e", fontSize: "11px", fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: "Revenue (£)",
        style: { fill: "#111", fontSize: "12px", fontWeight: 700 },
      },
      labels: {
        style: { fontSize: "12px", fontWeight: 600 },
        formatter: val => `£ ${val?.toLocaleString("en-GB")}`,
      },
    },
    grid: {
      borderColor: "#e8e8f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      style: { fontSize: "13px" },
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum = displayWeeks[dataPointIndex]

          const currentDate = dateByWeek[weekNum]
          const prevDate    = prevDateByWeek[weekNum]

          const resolvedCurrentDate =
            currentDate || (prevDate ? getSameWeekNextYear(prevDate) : null)
          const resolvedPrevDate =
            prevDate || (currentDate ? getSameWeekPreviousYear(currentDate) : null)

          const currentLabel = getWeekTooltipLabel(weekNum - 1, resolvedCurrentDate)
          const prevLabel    = getWeekTooltipLabel(weekNum - 1, resolvedPrevDate)

          return `Current Year : ${currentLabel || "N/A"}<br/> Previous Year : ${prevLabel || "N/A"}`
        },
      },
      y: {
        formatter: val =>
          val !== null && val !== undefined
            ? `£ ${val.toLocaleString("en-GB")}`
            : "N/A",
      },
    },
    legend: { show: false },
    responsive: [
      {
        breakpoint: 600,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              rotateAlways: true,
              style: { fontSize: "8px", fontWeight: 600 },
            },
          },
          yaxis: {
            labels: {
              style: { fontSize: "9px", fontWeight: 600 },
              formatter: val => `£${Math.round(val / 1000)}K`,
            },
          },
        },
      },
      {
        breakpoint: 900,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              rotateAlways: allWeeks.length > 15,
              style: { fontSize: "9px", fontWeight: 600 },
            },
          },
          yaxis: {
            labels: {
              style: { fontSize: "10px", fontWeight: 600 },
              formatter: val => `£${Math.round(val / 1000)}K`,
            },
          },
        },
      },
    ],
  }

  const currentWeekSet = new Set(salesRevenue.map(i => moment(i.date).isoWeek()))
  const matchedPrevious = (previousData || []).filter(i =>
    currentWeekSet.has(moment(i.date).isoWeek())
  )

  const currentTotal  = salesRevenue.reduce((s, i) => s + Number(i.amount), 0)
  const previousTotal = matchedPrevious.reduce((s, i) => s + Number(i.amount), 0)
  const diff          = currentTotal - previousTotal

  const currentAvg  = salesRevenue.length ? currentTotal / salesRevenue.length : 0
  const previousAvg = matchedPrevious.length ? previousTotal / matchedPrevious.length : 0
  const diffPct     = (((currentAvg - previousAvg) / maxValue) * 100).toFixed(1)

  const fmtGBP = v =>
    "£ " + Math.abs(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })

  const weekCurrent = currentWeekInput !== "" ? parseFloat(currentWeekInput) || 0 : null
  const weekPrev    = prevWeekInput !== "" ? parseFloat(prevWeekInput) || 0 : null
  const weekDiff    = weekCurrent !== null && weekPrev !== null ? weekCurrent - weekPrev : null

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 px-2 flex-wrap gap-3">
        <div>
          <h5 className="mb-1 fw-bold" style={{ color: "#1a1a2e", fontSize: "16px" }}>
            Sales Revenue History
          </h5>
        </div>

        <div className="d-flex gap-3 flex-wrap align-items-start">

          {/* ── Sort Order cycling button ── */}
          {(() => {
            const STATES = [
              {
                key: "chronological",
                icon: "⇅",
                label: "Sort",
                color: "#64748b",
                bg: "#f1f5f9",
                border: "#e2e8f0",
              },
              {
                key: "ascending",
                icon: "↑",
                label: "Low → High",
                color: "#2563eb",
                bg: "#eff6ff",
                border: "#bfdbfe",
              },
              {
                key: "descending",
                icon: "↓",
                label: "High → Low",
                color: "#0f766e",
                bg: "#f0fdfa",
                border: "#99f6e4",
              },
            ]
            const current = STATES.find(s => s.key === sortOrder) || STATES[0]
            const nextKey =
              sortOrder === "chronological"
                ? "ascending"
                : sortOrder === "ascending"
                ? "descending"
                : "chronological"

            return (
              <button
                onClick={() => setSortOrder(nextKey)}
                title={`Sort Order: ${current.label} — click to change`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: current.bg,
                  border: `1.5px solid ${current.border}`,
                  borderRadius: "12px",
                  padding: "9px 16px",
                  cursor: "pointer",
                  outline: "none",
                  transition: "all 0.18s ease",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  minWidth: "130px",
                }}
              >
                <span
                  style={{
                    fontSize: "18px",
                    lineHeight: 1,
                    color: current.color,
                    fontWeight: 700,
                    minWidth: "20px",
                    textAlign: "center",
                  }}
                >
                  {current.icon}
                </span>
                <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    Sort Order
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: current.color,
                      lineHeight: 1.4,
                    }}
                  >
                    {current.label}
                  </span>
                </span>
              </button>
            )
          })()}

          {/* ── Week comparison card — only shown in HistoryPage ── */}
          {showWeekCard && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "4px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "10px 16px",
                minWidth: "270px",
              }}
            >
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "2px" }}>
                Week Comparison
              </div>
              {/* Current Week input */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <FaDotCircle style={{ color: "#4361ee", fontSize: "10px", flexShrink: 0 }} />
                <span style={{ fontSize: "12px", color: "#374151", flexShrink: 0 }}>Current Week</span>
                <input
                  type="number"
                  value={currentWeekInput}
                  onChange={e => setCurrentWeekInput(e.target.value)}
                  placeholder="Enter value"
                  style={{
                    width: "100px",
                    fontSize: "12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    padding: "2px 6px",
                    outline: "none",
                    background: "#fff",
                    marginLeft: "auto",
                  }}
                />
              </div>
              {/* Previous Week input */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", borderTop: "1px dashed #e2e8f0", paddingTop: "4px" }}>
                <FaDotCircle style={{ color: "#4CBB17", fontSize: "10px", flexShrink: 0 }} />
                <span style={{ fontSize: "12px", color: "#374151", flexShrink: 0 }}>Prev Week</span>
                <input
                  type="number"
                  value={prevWeekInput}
                  onChange={e => setPrevWeekInput(e.target.value)}
                  placeholder="Enter value"
                  style={{
                    width: "100px",
                    fontSize: "12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    padding: "2px 6px",
                    outline: "none",
                    background: "#fff",
                    marginLeft: "auto",
                  }}
                />
              </div>
              {/* Difference row */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", borderTop: "1px dashed #e2e8f0", paddingTop: "4px" }}>
                <span style={{ fontSize: "12px", color: "#374151" }}>Difference</span>
                {weekDiff !== null ? (
                  <span style={{ fontSize: "13px", fontWeight: 700, color: weekDiff >= 0 ? "#15803d" : "#be123c", marginLeft: "auto" }}>
                    {weekDiff >= 0 ? "+" : "-"}{fmtGBP(weekDiff)}
                  </span>
                ) : (
                  <span style={{ fontSize: "12px", color: "#94a3b8", marginLeft: "auto" }}>—</span>
                )}
              </div>
            </div>
          )}

          {/* ── Stats grid: current year vs previous year ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "4px 24px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "10px 35px 10px 16px",
              minWidth: "300px",
              position: "relative",
            }}
          >
            {/* Formula tooltip icon */}
            <div
              style={{ position: "absolute", top: "8px", right: "10px" }}
              onMouseEnter={() => setShowFormula(true)}
              onMouseLeave={() => setShowFormula(false)}
            >
              <FaInfoCircle style={{ fontSize: "13px", color: "#94a3b8", cursor: "pointer" }} />
              {showFormula && (
                <div
                  style={{
                    position: "absolute",
                    top: "20px",
                    right: 0,
                    background: "#1e293b",
                    color: "#f1f5f9",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    fontSize: "12px",
                    lineHeight: "1.8",
                    zIndex: 999,
                    whiteSpace: "nowrap",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                    pointerEvents: "none",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#7dd3fc", marginBottom: "6px", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.6px" }}>% Calculation</div>
                  <div><span style={{ color: "#fde68a" }}>%</span> = (Weekly Avg ÷ Target) × 100</div>
                  <div style={{ borderTop: "1px solid #334155", marginTop: "6px", paddingTop: "6px" }}>
                    <div><span style={{ color: "#86efac" }}>Weekly Avg</span> = Total Revenue ÷ No. of Weeks</div>
                    <div><span style={{ color: "#86efac" }}>Target</span> = £{maxValue.toLocaleString("en-GB")}</div>
                  </div>
                </div>
              )}
            </div>
            {/* Row 1 — Current Year */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FaDotCircle style={{ color: "#4361ee", fontSize: "10px", flexShrink: 0 }} />
              <span style={{ fontSize: "12px", color: "#374151" }}>
                Current Year ({currentYear})
              </span>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e40af", marginLeft: "auto" }}>
                {fmtGBP(currentTotal)}
              </span>
            </div>
            {/* Row 2 — Previous Year */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", borderTop: "1px dashed #e2e8f0", paddingTop: "4px" }}>
              <FaDotCircle style={{ color: "#4CBB17", fontSize: "10px", flexShrink: 0 }} />
              <span style={{ fontSize: "12px", color: "#374151" }}>
                Previous Year ({previousYear})
              </span>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#166534", marginLeft: "auto" }}>
                {fmtGBP(previousTotal)}
              </span>
            </div>
            {/* Row 3 — Difference */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", borderTop: "1px dashed #e2e8f0", paddingTop: "4px" }}>
              <span style={{ fontSize: "12px", color: "#374151" }}>Difference</span>
              <span style={{ fontSize: "13px", fontWeight: 700, color: diff >= 0 ? "#15803d" : "#be123c", marginLeft: "auto" }}>
                {diff >= 0 ? "+" : "-"}{fmtGBP(diff)}
              </span>
              {diffPct !== null && (
                <span style={{ fontSize: "13px", fontWeight: 700, color: diff >= 0 ? "#15803d" : "#be123c" }}>
                  {diff >= 0 ? "▲" : "▼"} {Math.abs(diffPct)}%
                </span>
              )}
            </div>
          </div>

        </div>
      </div>

      <Chart options={options} series={series} type="area" height={320} />

      {/* Legend */}
      <div
        className="d-flex justify-content-center gap-4 mt-2"
        style={{ borderTop: "1px solid #f0f0f0", paddingTop: "12px" }}
      >
        {[
          { color: "#4361ee", label: `Current Year (${currentYear})`,  type: "line" },
          { color: "#4CBB17", label: `Previous Year (${previousYear})`, type: "dash" },
        ].map((l, i) => (
          <div key={i} className="d-flex align-items-center gap-1">
            {l.type === "line" ? (
              <span style={{ width: 16, height: 2, background: l.color, display: "inline-block" }} />
            ) : (
              <span style={{ width: 16, height: 2, borderTop: `2px dashed ${l.color}`, display: "inline-block" }} />
            )}
            <small style={{ color: "#1a1a2e", fontWeight: 600, fontSize: "11px" }}>
              {l.label}
            </small>
          </div>
        ))}
      </div>
    </div>
  )
}

export default SuccessRateChart
